/**
 * Which login Event Hub acts as — the decision, as plain data.
 *
 * Shared by the two places that need it and must agree: useEventHubLogin (what the pages compute
 * client-side, such as "is this my event") and the request middleware in EventHubClient (what the
 * server attributes a request to). Each derives it from the same inputs — the id token and the
 * stored View as target — so neither has to be told by the other.
 *
 * See useEventHubLogin for why a portal admin who is not an Event Hub admin stays on their own
 * login, and why roles are not part of the answer.
 */

import { canImpersonate, type EventHubRole } from './authorization';

export interface EventHubLogin {
  /** The login requests and ownership checks use. */
  login: string;
  /** True when that login is someone else's — the header is being sent. */
  impersonating: boolean;
  /**
   * True when the portal is viewing as someone but Event Hub cannot follow, because the caller
   * is not an event-hub-admin. `login` is then the caller's own.
   */
  unsupported: boolean;
}

export function eventHubLoginFor(input: {
  uid: string;
  targetAlias: string | null;
  isImpersonating: boolean;
  roles: ReadonlySet<EventHubRole>;
}): EventHubLogin {
  const { uid, targetAlias, isImpersonating, roles } = input;
  if (!isImpersonating || !targetAlias) {
    return { login: uid, impersonating: false, unsupported: false };
  }
  if (!canImpersonate(roles)) {
    return { login: uid, impersonating: false, unsupported: true };
  }
  return { login: targetAlias, impersonating: true, unsupported: false };
}
